/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { isEmpty } from 'lodash';
import Page from 'components/Page';
import { useDispatch, useSelector } from 'react-redux';
import { getAllAdminUsersApi, getUserByUserNameApi, updateUserAccessStatusApi } from 'redux/user/userApi';
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs';
import useAuth from 'hooks/useAuth';
import PermissionGuard from 'guards/PermissionGuard';

export default function CreatePrivilege({ route }) {
  const dispatch = useDispatch();
  const { user = {} } = useAuth();

  const [userSelected, setUserSelected] = useState('');
  const [selectedUserData, setSelectedUserData] = useState({});
  const { adminUsersList, selectedUser = {} } = useSelector(({ adminReducer }) => adminReducer);
  const [tabValue, setValue] = useState('employee');

  useEffect(() => {
    setUserSelected('');
    setSelectedUserData({});
    dispatch(
      getAllAdminUsersApi({
        roleBase: tabValue === 'b2b_Partner' ? 'OEM' : 'EMPLOYEE',
        oemId: tabValue === 'employee' ? user?.userName : ''
      })
    );
  }, [tabValue]);

  const handleChange = (value) => {
    const params = {};
    setUserSelected(value?.userName);
    params.userName = value?.userName;
    dispatch(getUserByUserNameApi(params));
  };

  const handleStatusChange = async (key, entry, value) => {
    const updatedData = JSON.parse(JSON.stringify(selectedUserData));
    updatedData.accessList[key][entry] = value;
    setSelectedUserData(updatedData);
    const userName = selectedUser?.userName;
    const accessListKey = key;
    const accessListEntry = entry;
    const accessListValue = value;
    const data = {
      userName,
      accessListKey,
      accessListEntry,
      accessListValue
    };
    await dispatch(updateUserAccessStatusApi(data));
  };

  useEffect(() => {
    if (!isEmpty(selectedUser)) {
      setSelectedUserData(selectedUser);
    }
  }, [selectedUser]);

  const handleTabChange = (newValue) => {
    setValue(newValue);
  };

  const renderTable = (accessGroups) => (
    <TableContainer component={Paper} sx={{ marginBottom: '12px' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="52%">{accessGroups.group}</TableCell>
            <TableCell align="center">CREATE</TableCell>
            <TableCell align="center">READ</TableCell>
            <TableCell align="center">UPDATE</TableCell>
            <TableCell align="center">DELETE</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(selectedUserData.accessList).map(([key, value], keyIndex) => {
            const isB2BPartnerExcluded =
              tabValue === 'b2b_Partner' &&
              (user?.oemId !== 'SERVICEPLUG' || !isEmpty(user?.oemId)) &&
              [
                'ADMIN_PRODUCT_AND_SERVICES',
                'PRELIST_PRODUCT_AND_SERVICES',
                'B2B_PARTNER_PRODUCT',
                'PLUS_FEATURES_EVENT',
                'PLUS_FEATURES_OFFERS',
                'PLUS_FEATURES_BUSINESS_OPPORTUNITIES',
                'VEHICLES_OWNED',
                'PLUS_FEATURES',
                'CATEGORY',
                'ADMIN_ANALYTICS',
                'PLUS_FEATURES_SCHOOL_OF_AUTO',
                'CUSTOMER',
                'B2B_PARTNERS'
              ].includes(key);

            const isEmployeeExcluded =
              tabValue === 'employee' &&
              user?.role !== 'OEM' &&
              (user?.oemId === 'SERVICEPLUG' || isEmpty(user?.oemId)) &&
              ['OEM_OFFERS', 'OEM_PRODUCTS', 'USER_PROFILE', 'OEM_ANALYTICS'].includes(key);

            if (
              user?.accessList?.[key]?.READ &&
              accessGroups.keys.includes(key) &&
              !isB2BPartnerExcluded &&
              !isEmployeeExcluded
            ) {
              return (
                <TableRow key={keyIndex}>
                  <TableCell component="th" scope="row" width="52%">
                    {key}
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={value.CREATE}
                      onChange={() => handleStatusChange(key, 'CREATE', !value.CREATE)}
                      sx={{ color: 'green' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={value.READ}
                      onChange={() => handleStatusChange(key, 'READ', !value.READ)}
                      sx={{ color: 'green' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={value.UPDATE}
                      onChange={() => handleStatusChange(key, 'UPDATE', !value.UPDATE)}
                      sx={{ color: 'green' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={value.DELETE}
                      onChange={() => handleStatusChange(key, 'DELETE', !value.DELETE)}
                      sx={{ color: 'green' }}
                    />
                  </TableCell>
                </TableRow>
              );
            }
            return null;
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const accessGroups = [
    {
      group: 'SALES',
      keys: [
        'DASHBOARD',
        'STORES',
        'CUSTOMER',
        'VEHICLES_BUY_SELL',
        'VEHICLES_OWNED',
        'B2B_PARTNER_PRODUCT',
        'PRELIST_PRODUCT_AND_SERVICES',
        'ADMIN_PRODUCT_AND_SERVICES',
        'OEM_PRODUCTS',
        'NEW_VEHICLES'
      ]
    },
    {
      group: 'MARKETING',
      keys: [
        'ADMIN_ANALYTICS',
        'OEM_ANALYTICS',
        'PLUS_FEATURES',
        'PLUS_FEATURES_EVENT',
        'PLUS_FEATURES_OFFERS',
        'PLUS_FEATURES_BUSINESS_OPPORTUNITIES',
        'PLUS_FEATURES_SCHOOL_OF_AUTO',
        'RATING_AND_REVIEWS',
        'ADVERTISEMENTS',
        'OEM_OFFERS',
        'REPORT',
        'REPORT_NOTES'
      ]
    },
    {
      group: 'ADMIN',
      keys: ['PERMISSION', 'USER', 'USER_PROFILE', 'CATEGORY', 'B2B_PARTNERS']
    }
  ];

  return (
    <>
      {user?.accessList?.PERMISSION?.READ ? (
        <Page title="Permission | Serviceplug">
          <Container maxWidth="xl" sx={{ mt: '25px', px: '8px !important' }}>
            <HeaderBreadcrumbs
              heading="Permission Manager"
              links={[{ name: 'Permission Access' }]}
              action={
                <>
                  {user?.role !== 'OEM' && (
                    <>
                      <Chip
                        label="B2B Partner"
                        size="large"
                        color="primary"
                        variant={tabValue === 'b2b_Partner' ? 'contained' : 'outlined'}
                        style={{ marginLeft: 4 }}
                        onClick={() => handleTabChange('b2b_Partner')}
                      />
                      <Chip
                        label="EMPLOYEE"
                        size="large"
                        color="primary"
                        variant={tabValue === 'employee' ? 'contained' : 'outlined'}
                        style={{ marginLeft: 4 }}
                        onClick={() => handleTabChange('employee')}
                      />
                    </>
                  )}
                </>
              }
              sx={{ mb: 0 }}
            />
            <Autocomplete
              options={adminUsersList}
              filterSelectedOptions
              value={userSelected}
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              onChange={(event, value) => handleChange(value)}
              renderInput={(params) => <TextField {...params} fullWidth label="Select User *" sx={{ mb: 2 }} />}
              sx={{ marginTop: '24px' }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography>{option.userName}</Typography>
                    <Typography>{tabValue !== 'employee' ? `(${option.businessName})` : option.ownerName}</Typography>
                    {tabValue === 'employee' && (
                      <Chip
                        label={option?.employeeId}
                        size="small"
                        color="primary"
                        variant="contained"
                        style={{ marginLeft: 4 }}
                      />
                    )}
                  </Stack>
                </li>
              )}
            />
            {!isEmpty(selectedUserData) &&
              accessGroups.map((group, index) => <div key={index}>{renderTable(group)}</div>)}
          </Container>
        </Page>
      ) : (
        <PermissionGuard />
      )}
    </>
  );
}
